import { Link, useParams } from "react-router-dom";
import { useMemo } from "react";
import SubsWall, { STYLE_PALETTES, type SubTile, type TileStyle } from "../components/SubsWall";
import "./ProfileV2.css";

// ----------------------------------------------------------------------------
// Mock creator + wall — replaced by /api/creator/:handle in a later pass.
// ----------------------------------------------------------------------------

function mockProfile(handle: string) {
  const display = handle.charAt(0).toUpperCase() + handle.slice(1);
  const seed: Array<Partial<SubTile> & { name: string; message: string; monthlyCents: number; style: TileStyle }> = [
    { name: "Sarah K.",        message: "Your masterclass on UX writing changed my career.",          monthlyCents: 500,   style: "card" },
    { name: "Marcus T.",       message: "Listening on every commute. Two years and counting.",        monthlyCents: 500,   style: "card" },
    { name: "Priya N.",        message: "Best long-form analysis on YouTube. Period.",                monthlyCents: 500,   style: "card" },
    { name: "Hiro Tanaka",     message: "Your camera reviews are why I bought the GFX.",              monthlyCents: 1000,  style: "inked" },
    { name: "Lena R.",         message: "I quote you in my own lectures (with credit).",              monthlyCents: 1000,  style: "inked" },
    { name: "Dr. Owen Hayes",  message: "Reading you at 6am with coffee. Worth every cent.",          monthlyCents: 2500,  style: "signature", anniversaryMonths: 14 },
    { name: "Camille Joubert", message: "Subscribed since the 'Why I Quit Google' video.",            monthlyCents: 2500,  style: "signature" },
    { name: "M.J. Asante",     message: "Your interview series is the new long-form journalism.",     monthlyCents: 5000,  style: "gilded", anniversaryMonths: 26 },
    { name: "Helena Voss",     message: "Patron of the channel since '23. Quality compounds.",        monthlyCents: 10000, style: "gilded", anniversaryMonths: 30 },
    { name: "James Lockwood",  message: "Founding member · supporting the work since day one.",       monthlyCents: 25000, style: "engraved", founderNumber: 1, anniversaryMonths: 36 },
  ];
  const tiles: SubTile[] = seed.map((s, i) => ({
    id: `t${i}`,
    name: s.name,
    message: s.message,
    style: s.style,
    color: STYLE_PALETTES[s.style][(i + 2) % 8],
    xPct: 12 + ((i * 71) % 76),
    yPct: 18 + ((i * 47) % 68),
    monthlyCents: s.monthlyCents,
    anniversaryMonths: s.anniversaryMonths ?? 0,
    founderNumber: s.founderNumber ?? null,
    boostUntil: null,
    fadeStartedAt: null,
    badge: null,
  }));

  const mrr = tiles.reduce((sum, t) => sum + t.monthlyCents / 100, 0);
  return {
    handle,
    display_name: display,
    bio: "Long-form essays and interviews about the craft of building. Two videos a month, no filler.",
    youtube_handle: `@${handle}`,
    youtube_url: `https://youtube.com/@${handle}`,
    member_since: "June 2023",
    supporter_count: tiles.length,
    monthly_recurring: mrr,
    tiles,
  };
}

export default function ProfileV2(): JSX.Element {
  const { handle = "demo" } = useParams<{ handle: string }>();
  const profile = useMemo(() => mockProfile(handle), [handle]);

  return (
    <div className="at-page pv2">
      {/* Magazine-style nav */}
      <nav className="pv2-nav at-container">
        <Link to="/" className="pv2-brand">
          <span className="pv2-brand-mark">◆</span> SubsWall
        </Link>
        <div className="pv2-nav-right">
          <Link to="/login">Sign in</Link>
          <Link to={`/${handle}/sign`} className="at-btn at-btn-primary">Sign the wall</Link>
        </div>
      </nav>

      {/* Masthead */}
      <header className="pv2-mast at-container">
        <div className="pv2-mast-left">
          <div className="pv2-av">{profile.display_name[0]}</div>
        </div>
        <div className="pv2-mast-mid">
          <div className="at-eyebrow">Creator wall</div>
          <h1 className="at-display pv2-h1">{profile.display_name}</h1>
          <div className="pv2-channel">
            <a href={profile.youtube_url} target="_blank" rel="noopener noreferrer">
              {profile.youtube_handle} on YouTube ↗
            </a>
          </div>
          <p className="at-lead pv2-bio">{profile.bio}</p>
        </div>
        <div className="pv2-mast-right">
          <div className="pv2-stat">
            <div className="at-eyebrow">Supporters</div>
            <div className="pv2-stat-num at-mono">{profile.supporter_count}</div>
          </div>
          <div className="pv2-stat">
            <div className="at-eyebrow">Monthly</div>
            <div className="pv2-stat-num at-mono">${profile.monthly_recurring.toLocaleString()}</div>
          </div>
          <div className="pv2-stat">
            <div className="at-eyebrow">Since</div>
            <div className="pv2-stat-sub at-mono">{profile.member_since}</div>
          </div>
        </div>
      </header>

      {/* The wall itself */}
      <section className="pv2-wall-section at-container">
        <SubsWall tiles={profile.tiles} theme="atelier" />
      </section>

      {/* CTA strip */}
      <section className="pv2-cta at-container">
        <div className="pv2-cta-text">
          <h2 className="at-display pv2-h2">Sign the wall.</h2>
          <p className="at-lead">
            Pick an amount, write a line, drop your tile on the wall. Stay as
            long as you support — it's that simple.
          </p>
        </div>
        <Link to={`/${handle}/sign`} className="at-btn at-btn-primary pv2-cta-btn">
          Become a supporter →
        </Link>
      </section>

      {/* Recent supporters list */}
      <section className="pv2-list at-container">
        <div className="at-eyebrow">Recent supporters</div>
        <h2 className="at-display pv2-h2">Standing with {profile.display_name}.</h2>
        <ul className="pv2-supporter-list">
          {profile.tiles.slice(0, 10).map((t) => (
            <li key={t.id}>
              <div className="pv2-sup-name">{t.name}</div>
              <div className="pv2-sup-msg">"{t.message}"</div>
              <div className="pv2-sup-amt at-mono">${(t.monthlyCents / 100).toFixed(0)}/mo</div>
            </li>
          ))}
        </ul>
      </section>

      <footer className="pv2-foot">
        <div className="at-container pv2-foot-inner">
          <div className="pv2-brand">◆ SubsWall · 4.9% take, 95.1% to creators</div>
          <Link to="/">Home</Link>
        </div>
      </footer>
    </div>
  );
}
